import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import { apiClient, type ApiError, type ApiPaginatedResponse } from "../api-client";
import {
  pathToQueryKey,
  pathToInvalidationPrefix,
} from "@kubdee/shared/query/path-query-key";
import { CROSS_CUTTING_QUERY_KEYS } from "@kubdee/shared/constants/cross-cutting-queries";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseApiQueryOptions<T>
  extends Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn"> {
  /** Query params appended to the URL and to the auto-derived query key. */
  params?: Record<string, unknown>;
  /** Extra headers for custom use cases. */
  headers?: Record<string, string>;
}

export interface UseApiMutationOptions<TData, TVariables>
  extends Omit<UseMutationOptions<TData, ApiError, TVariables>, "mutationFn"> {
  /**
   * Extra query keys to refetch on success — use only for *cross-module*
   * writes that touch a collection the mutation's own path doesn't cover.
   *
   * Queries matching `pathToInvalidationPrefix(path)` are already refetched
   * automatically, as are every entry in `CROSS_CUTTING_QUERY_KEYS`. Listing
   * the mutation's own collection here is redundant and will be removed in
   * Phase 4 cleanup.
   */
  invalidateKeys?: QueryKey[];
  /**
   * Whether to auto-refetch cross-cutting queries (activities, comments,
   * notes, tasks, ...) on success. Default `true`. Pass `false` only for
   * hot-path mutations where cross-cutting refresh is noise.
   */
  invalidateCrossCutting?: boolean;
  /** Extra headers for custom use cases. */
  headers?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// useApiQuery — GET wrapper
// ---------------------------------------------------------------------------

/**
 * Thin wrapper around `useQuery` that fetches from `apiClient.get(path)`.
 *
 * The query key is auto-derived from the path via `pathToQueryKey`, so callers
 * only need to pass the path and (optionally) request params. Query params are
 * appended to the key as the final segment so pagination/filter changes create
 * distinct cache entries.
 *
 * @example
 *   const { data } = useApiQuery<UserListResponse>("/core/users", {
 *     params: { page: 1, perPage: 20 },
 *   });
 */
export function useApiQuery<T>(
  path: string,
  options?: UseApiQueryOptions<T>,
) {
  const { params, headers, ...queryOptions } = options ?? {};

  return useQuery<T, ApiError>({
    queryKey: pathToQueryKey(path, params),
    queryFn: () => apiClient.get<T>(path, params, { headers }),
    ...queryOptions,
  });
}

// ---------------------------------------------------------------------------
// useApiInfiniteQuery — GET wrapper for paginated endpoints (Load More)
// ---------------------------------------------------------------------------

export interface UseApiInfiniteQueryOptions<T>
  extends Omit<
    UseInfiniteQueryOptions<
      ApiPaginatedResponse<T>,
      ApiError,
      InfiniteData<ApiPaginatedResponse<T>>,
      QueryKey,
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  > {
  /** Query params appended to the URL and to the auto-derived query key. The `page` param is managed by the hook. */
  params?: Record<string, unknown>;
  /** Extra headers for custom use cases. */
  headers?: Record<string, string>;
}

/**
 * Infinite-query wrapper for endpoints returning `ApiPaginatedResponse<T>`.
 * Computes `hasNextPage` from `meta.totalPages` (or `meta.total + meta.perPage`
 * as a fallback) so callers can drive a Load More button without duplicating
 * the math in every module.
 *
 * Callers should NOT pass `page` inside `params` — it is injected automatically
 * from `pageParam`.
 *
 * @example
 *   const q = useApiInfiniteQuery<FleetVehicle>("/fleet/vehicles", {
 *     params: { companyId, perPage: pageSize, search },
 *   });
 *   const items = q.data?.pages.flatMap((p) => p.data) ?? [];
 */
export function useApiInfiniteQuery<T>(
  path: string,
  options?: UseApiInfiniteQueryOptions<T>,
) {
  const { params, headers, ...queryOptions } = options ?? {};

  return useInfiniteQuery<
    ApiPaginatedResponse<T>,
    ApiError,
    InfiniteData<ApiPaginatedResponse<T>>,
    QueryKey,
    number
  >({
    queryKey: pathToQueryKey(path, params),
    queryFn: ({ pageParam }) =>
      apiClient.get<ApiPaginatedResponse<T>>(
        path,
        { ...params, page: pageParam },
        { headers },
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage.meta;
      if (!meta || typeof meta.page !== "number") return undefined;
      const total = meta.total ?? 0;
      const perPage = meta.perPage ?? 0;
      const totalPages =
        meta.totalPages ?? (perPage > 0 ? Math.ceil(total / perPage) : 0);
      return meta.page < totalPages ? meta.page + 1 : undefined;
    },
    ...queryOptions,
  });
}

// ---------------------------------------------------------------------------
// useApiMutation — POST / PUT / PATCH / DELETE wrapper
// ---------------------------------------------------------------------------

type HttpMethod = "post" | "put" | "patch" | "delete";

/**
 * Extract the entity id from a mutation response.
 * Supports both `{ data: { id, ... } }` and `{ id, ... }` shapes.
 * Returns undefined for empty/missing ids.
 */
function extractEntityId(response: unknown): string | undefined {
  if (!response || typeof response !== "object") return undefined;
  const res = response as { data?: { id?: unknown }; id?: unknown };
  if (
    res.data &&
    typeof res.data === "object" &&
    typeof res.data.id === "string" &&
    res.data.id.length > 0
  ) {
    return res.data.id;
  }
  if (typeof res.id === "string" && res.id.length > 0) return res.id;
  return undefined;
}

/**
 * Check if a query key looks like a detail query ending with the given id.
 * Matches shapes like `[..., id]` or `[..., id, "subresource"]`.
 */
function isDetailQueryKey(queryKey: readonly unknown[], id: string): boolean {
  const len = queryKey.length;
  if (len < 2) return false;
  if (queryKey[len - 1] === id) return true;
  if (len >= 3 && queryKey[len - 2] === id) return true;
  return false;
}

/**
 * Thin wrapper around `useMutation` that calls `apiClient[method](path, body)`.
 *
 * Automatic cache behavior on success:
 *   1. If the response contains `data.id`, any active detail query matching
 *      that id is updated directly with the response (instant UI update).
 *   2. All queries under `pathToInvalidationPrefix(path)` are refetched
 *      (active only) — this covers the mutation's own collection and any
 *      detail/sub-resource queries under it.
 *   3. Every entry in `CROSS_CUTTING_QUERY_KEYS` is refetched (active only)
 *      so activity timelines, comments, etc. stay fresh across modules.
 *      Pass `invalidateCrossCutting: false` to opt out.
 *   4. Any extra keys listed in `invalidateKeys` are refetched. Only use
 *      this for *cross-module* writes that can't be derived from the path.
 *
 * @example
 *   const create = useApiMutation<CreateBookingResponse, CreateBookingInput>(
 *     "post",
 *     "/car-rental/bookings",
 *     // cross-module: creating a booking also frees/books a vehicle
 *     { invalidateKeys: [["fleet", "vehicles"]] },
 *   );
 */
export function useApiMutation<TData, TVariables = unknown>(
  method: HttpMethod,
  path: string,
  options?: UseApiMutationOptions<TData, TVariables>,
) {
  const queryClient = useQueryClient();
  const {
    invalidateKeys,
    invalidateCrossCutting = true,
    headers,
    onSuccess,
    ...mutationOptions
  } = options ?? {};

  return useMutation<TData, ApiError, TVariables>({
    mutationFn: (variables) => {
      if (method === "delete") {
        // DELETE carries identifiers via the query string (RFC 9110 discourages
        // request bodies on DELETE) — pass the caller's variables through as
        // params so hooks like `useDisconnectInstance({ companyId })` work.
        return apiClient.delete<TData>(
          path,
          variables as Record<string, unknown> | undefined,
          { headers },
        );
      }
      return apiClient[method]<TData>(path, variables, { headers });
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      // 1. Auto-update detail queries matching entity.id from response
      //    (skip for DELETE since the entity no longer exists).
      if (method !== "delete") {
        const entityId = extractEntityId(data);
        if (entityId) {
          const cache = queryClient.getQueryCache();
          const activeQueries = cache.findAll({ type: "active" });
          for (const query of activeQueries) {
            if (isDetailQueryKey(query.queryKey, entityId)) {
              queryClient.setQueryData(query.queryKey, data);
            }
          }
        }
      }

      // 2. Auto-invalidate the mutation's own collection + sub-resources.
      //    Use invalidateQueries (not refetchQueries) so inactive/cached
      //    queries are marked stale too — otherwise navigating back to a
      //    page after a mutation elsewhere shows the previous data
      //    (e.g. delete payment from /payments/:id, return to invoice
      //    detail still listing the draft until manual reload).
      //    Active queries refetch immediately; inactive ones refetch on
      //    next mount.
      const prefix = pathToInvalidationPrefix(path);
      if (prefix.length > 0) {
        queryClient.invalidateQueries({ queryKey: prefix });
      }

      // 3. Auto-invalidate cross-cutting timelines (opt-out).
      if (invalidateCrossCutting) {
        for (const key of CROSS_CUTTING_QUERY_KEYS) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }

      // 4. Caller-provided extra keys (cross-module writes).
      if (invalidateKeys?.length) {
        for (const key of invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }

      // 5. Forward to caller's onSuccess.
      onSuccess?.(data, variables, onMutateResult, context);
    },
    ...mutationOptions,
  });
}
